import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import { useListOrgLicenses } from '@/features/Licenses/api/licenseHooks'
import { useCanManageLicenses } from '@/features/Licenses/hooks/useCanManageLicenses'
import { useFindHivesByOrgId } from '@/generated/http-clients/v2ApiComponents'
import type {
  UserDetail,
  UserOrganization,
} from '@/generated/http-clients/v3ApiSchemas'
import {
  Badge,
  Box,
  Button,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * One card per org in the user's `organizations` array. Renders ONLY
 * org-level signals — fields that vary across orgs of the same user — so the
 * card actually disambiguates between them:
 *
 *   - Owner vs member         — from `org.ownerId === user.id`
 *   - Cluster count           — from `useFindHivesByOrgId`
 *   - License count (gated)   — from `useListOrgLicenses` (stub;
 *                                becomes the generated hook post
 *                                APP-1322)
 *
 * `activationStatus` lives on the user (`UserDetail.activationStatus`)
 * and is therefore identical across every org of the same user — so it is
 * NOT rendered here. The existing `UserAccount` view surfaces it once at
 * the user level, which is the correct granularity (per
 * org-disambiguation-picker/spec.md and SR-002 in
 * docs/spec-review-checklist.md).
 *
 * Primary action: an "Open org" button navigating to Org Details. The
 * label is the literal text "Open org" (no Unicode arrow); the
 * `<ArrowRight/>` icon supplies the directional cue (per design.md
 * Decision 17 — no glyph + icon duplication).
 *
 * Spec reference: openspec/changes/add-slg-m1-backoffice-ui/specs/
 * org-disambiguation-picker/spec.md
 */

type Props = {
  user: UserDetail
  org: UserOrganization
}

export function OrgDisambiguationCard({ user, org }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()
  const { canManage: canManageLicenses } = useCanManageLicenses()

  const hivesQuery = useFindHivesByOrgId({
    pathParams: { orgId: org.orgId },
  })
  const licensesQuery = useListOrgLicenses(
    {
      pathParams: { orgId: org.orgId },
      queryParams: { limit: 1 },
    },
    { enabled: canManageLicenses },
  )

  const clusterCount = hivesQuery.data?.hives_info?.length ?? 0
  const licenseCount = licensesQuery.data?.total ?? 0
  const isOwner = Boolean(user.id) && org.ownerId === user.id

  const openOrg = () =>
    navigate({
      to: hivesOverviewOrganizationDetailsRoute.to,
      params: { orgId: org.orgId },
    })

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      width="100%"
    >
      <VStack align="stretch" gap={3}>
        <HStack justify="space-between" align="flex-start">
          <VStack align="start" gap={1}>
            <Text fontWeight="bold" fontSize="md">
              {org.orgName || org.orgId}
            </Text>
            {org.orgName && (
              <Text fontSize="xs" color="text.text-subtle" fontFamily="mono">
                {org.orgId}
              </Text>
            )}
          </VStack>
          <Button size="sm" colorPalette="brand" onClick={openOrg}>
            {t('licenses.orgDisambiguationCard.openOrg')}
            <ArrowRight size={14} />
          </Button>
        </HStack>

        <HStack gap={2} wrap="wrap">
          <Badge
            colorPalette={isOwner ? 'blue' : 'gray'}
            variant="subtle"
            size="sm"
          >
            {isOwner
              ? t('licenses.orgDisambiguationCard.ownerTag')
              : t('licenses.orgDisambiguationCard.memberTag')}
          </Badge>
        </HStack>

        <HStack gap={6} fontSize="sm" color="text.text-subtle">
          {hivesQuery.isLoading ? (
            <Skeleton h={4} w={20} />
          ) : (
            <Text>
              {t('licenses.orgDisambiguationCard.clusters')}: {clusterCount}
            </Text>
          )}
          {canManageLicenses &&
            (licensesQuery.isLoading ? (
              <Skeleton h={4} w={24} />
            ) : (
              <Text>
                {t('licenses.orgDisambiguationCard.licenses')}: {licenseCount}
              </Text>
            ))}
        </HStack>
      </VStack>
    </Box>
  )
}

/**
 * Wrapper that renders the list of cards. Handles the empty-orgs case per
 * the spec (M1 boundary — account creation deferred to M3).
 */
type CardsProps = {
  user: UserDetail
}

export function OrgDisambiguationCards({ user }: Readonly<CardsProps>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const orgs = user.organizations ?? []

  if (orgs.length === 0) {
    return (
      <Text fontSize="sm" color="text.text-subtle">
        {t('licenses.orgDisambiguationCard.emptyState')}
      </Text>
    )
  }

  return (
    <VStack align="stretch" gap={3} width="100%">
      {orgs.map((org) => (
        <OrgDisambiguationCard key={org.orgId} user={user} org={org} />
      ))}
    </VStack>
  )
}
